// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Typeahead.tsx
 * @input Uses React, BaseTypeahead, Field, Token, InputGroupContext
 * @output Exports Typeahead styled typeahead component
 * @position Styled wrapper; composes BaseTypeahead with Field or InputGroup
 *
 * Owns the input wrapper (border, padding, status styles), selected value
 * token with spacing compensation, and edit mode behavior. Delegates
 * search, keyboard navigation, and dropdown to BaseTypeahead.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Typeahead/index.ts
 * - /apps/storybook/stories/Typeahead.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Typeahead/ (showcase blocks)
 */
import React, { useCallback, useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { BaseTypeahead } from "./BaseTypeahead.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { Field, InputClearButton, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Token } from "../Token/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { renderIconSlot } from "../Icon/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import "../theme/tokens.stylex.js";
import { spacingVars, sizeVars } from "../theme/tokens.stylex.js";
import { groupStyles } from "../InputGroup/groupStyles.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { getInputARIA, mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  wrapper: {
    kVAEAm: "x1n2onr6",
    kwnvtZ: "x1a02dak",
    kOIVth: "xzye2dw",
    k8WAf4: "x1x7cmac",
    kkrTdU: "x1ed109x",
    $$css: true
  },
  token: {
    kogj98: "x1n5sqmh",
    $$css: true
  },
  clearButton: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x1nzz77m",
    kt4wiu: "x1oinnt5",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  clearButtonSm: {
    k87sOh: "xeqxqpg",
    kt4wiu: "x8qlsd8",
    $$css: true
  },
  inputHidden: {
    kzqmXN: "xnalus7",
    k7Eaqz: "xeuugli",
    kUk6DE: "x1somy94",
    kmVPX3: "x1717udv",
    kSiTet: "xg01cxk",
    kVAEAm: "x10l6tqk",
    $$css: true
  }
};
const wrapperSizeStyles = {
  sm: {
    kAzted: "x1gdqauq",
    $$css: true
  },
  md: {
    kAzted: "x1uogy3y",
    $$css: true
  },
  lg: {
    kAzted: "x1xf4nls",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * A search-as-you-type component for selecting an item from a search source.
 *
 * Wraps BaseTypeahead with Field for label, description, and status.
 * Owns the input wrapper styling, selected value token, and edit mode.
 *
 * Edit mode: clicking the token or input area removes the token, populates
 * the input with the value's label, and selects all text. Blurring without
 * selecting restores the original token. Escape also restores.
 *
 * @example
 * ```
 * <Typeahead
 *   label="Assignee"
 *   searchSource={userSource}
 *   value={assignee}
 *   onChange={setAssignee}
 *   placeholder="Search users..."
 * />
 * ```
 */
export function Typeahead({
  ref,
  label,
  isLabelHidden = false,
  description,
  isRequired = false,
  isOptional = false,
  status,
  statusVariant = 'attached',
  startIcon,
  labelTooltip,
  searchSource,
  value,
  onChange,
  renderItem,
  placeholder,
  hasEntriesOnFocus,
  maxMenuItems,
  emptySearchResultsText,
  isDisabled = false,
  disabledMessage,
  hasClear = true,
  hasAutoFocus,
  size: sizeProp,
  debounceMs,
  onChangeQuery,
  onOpenChange,
  width,
  xstyle,
  className,
  style,
  'data-testid': testId
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const inputId = useId();
  const inputLabelId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const inputGroup = useInputGroup();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const tokenRef = useRef(null);

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input wrapper (which already exists) and
  // the input stays perceivable via aria-disabled + readOnly instead of the
  // disabled attribute. Editing and selection stay blocked by the isDisabled
  // guards (handleWrapperClick, handleEnterEditMode, and BaseTypeahead's own
  // focus/change guards).
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The wrapper div is not naturally focusable; focusin bubbles up from the
    // input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });

  // Edit mode: when the user clicks the token to edit the selected value
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(null);

  // Show token when value is selected and not in edit mode
  const showToken = value != null && !isEditing;

  // Enter edit mode: remove token visually, populate input with value label
  const handleEnterEditMode = useCallback(() => {
    if (isDisabled || !value) {
      return;
    }
    setEditingValue(value);
    setIsEditing(true);
    // The base will receive onChangeQuery with the value's label
    onChangeQuery?.(value.label);
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input) {
        // Set the input value directly since the base manages its own query state
        // We trigger a synthetic change to sync the base's internal state
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        nativeInputValueSetter?.call(input, value.label);
        input.dispatchEvent(new Event('input', {
          bubbles: true
        }));
        input.focus();
        input.setSelectionRange(0, input.value.length);
      }
    });
  }, [isDisabled, value, onChangeQuery]);

  // Handle blur: restore token if editing and no selection was made
  const handleBlur = useCallback(e => {
    // Don't restore if focus is moving within the wrapper (e.g. to dropdown)
    if (wrapperRef.current?.contains(e.relatedTarget)) {
      return;
    }
    if (editingValue && isEditing) {
      setIsEditing(false);
      setEditingValue(null);
      // Value was never cleared from parent, so no onChange needed
    }
  }, [editingValue, isEditing]);

  // Handle selection from dropdown — clears edit mode
  const handleChange = useCallback(item => {
    setIsEditing(false);
    setEditingValue(null);
    onChange(item);
    // After selection, focus the token so keyboard users stay in the component.
    // Use requestAnimationFrame because the token renders on the next cycle.
    if (item) {
      requestAnimationFrame(() => {
        const tokenEl = tokenRef.current;
        if (tokenEl) {
          // Focus the internal button inside the token
          const button = tokenEl.querySelector('button');
          (button ?? tokenEl).focus();
        }
      });
    }
  }, [onChange]);

  // Handle clear (explicit X button on token)
  const handleClear = useCallback(() => {
    setIsEditing(false);
    setEditingValue(null);
    onChange(null);
    inputRef.current?.focus();
  }, [onChange]);

  // Handle Escape during edit mode — restore token
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Escape' && editingValue) {
      e.preventDefault();
      setIsEditing(false);
      setEditingValue(null);
      inputRef.current?.blur();
    }
  }, [editingValue]);

  // Click wrapper to focus input or enter edit mode
  const handleWrapperClick = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (showToken) {
      handleEnterEditMode();
    } else {
      inputRef.current?.focus();
    }
  }, [isDisabled, showToken, handleEnterEditMode]);
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelId, [description ? descriptionId : null, status?.message ? statusMessageId : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);
  const sizeStyle = wrapperSizeStyles[size];
  const typeaheadContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: mergeRefs(wrapperRef, disabledMessageTooltip.ref, inputGroup ? ref : undefined),
      "data-testid": testId,
      onClick: handleWrapperClick,
      onBlur: handleBlur,
      ...mergeProps(themeProps('typeahead', {
        size,
        status: status?.type
      }), stylex.props(inputWrapperStyles.base, styles.wrapper, sizeStyle, status && inputStatusBorderStyles[status.type], status && !isDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], isDisabled && inputWrapperStyles.disabled, inputGroup && groupStyles.inGroup, inputGroup && xstyle), inputGroup ? className : undefined, inputGroup ? style : undefined),
      children: [startIcon && renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
        id: inputLabelId,
        children: label
      }), showToken && /*#__PURE__*/_jsx(Token, {
        ref: tokenRef,
        label: value.label,
        size: size,
        onClick: handleEnterEditMode,
        isDisabled: isDisabled,
        xstyle: styles.token
      }), /*#__PURE__*/_jsx(BaseTypeahead, {
        ref: inputRef,
        searchSource: searchSource,
        value: value,
        onChange: handleChange,
        renderItem: renderItem,
        placeholder: showToken ? undefined : placeholder,
        hasEntriesOnFocus: hasEntriesOnFocus,
        maxMenuItems: maxMenuItems,
        emptySearchResultsText: emptySearchResultsText,
        isDisabled: isDisabled,
        hasAutoFocus: hasAutoFocus,
        isFocusableDisabled: showsDisabledMessage,
        inputId: inputId,
        ariaDescribedBy: ariaDescribedBy,
        ariaLabelledBy: ariaLabelledBy,
        onChangeQuery: onChangeQuery,
        onOpenChange: onOpenChange,
        debounceMs: debounceMs,
        anchorRef: wrapperRef,
        onKeyDown: handleKeyDown,
        inputXStyle: showToken ? styles.inputHidden : undefined
        // While the token is shown the input is collapsed (width 0 /
        // opacity 0) — take it out of the Tab order so keyboard users
        // don't hit an invisible stop (WCAG 2.4.3 / 2.4.7). It stays
        // programmatically focusable: entering edit mode and clearing
        // both refocus it after it uncollapses.
        ,
        inputTabIndex: showToken ? -1 : undefined,
        size: size
      }), hasClear && value && !isDisabled && /*#__PURE__*/_jsx(InputClearButton, {
        label: t('@astryx.typeahead.clearSelection'),
        onClick: e => {
          e.stopPropagation();
          handleClear();
        },
        xstyle: [styles.clearButton, size === 'sm' && styles.clearButtonSm]
      })]
    }), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
  if (inputGroup) {
    return typeaheadContent;
  }
  return /*#__PURE__*/_jsx(Field, {
    ref: ref,
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: inputId,
    descriptionID: description ? descriptionId : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    xstyle: xstyle,
    className: className,
    style: style,
    children: typeaheadContent
  });
}
Typeahead.displayName = 'Typeahead';